import { ComponentProps, FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import * as _ from "lodash";

import { useGoogleAnalytics } from "@ctra/analytics";
import { Enterprise, SectionEntity } from "@ctra/api";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Button, Menu, Row, Col, Spin, Analysis, Icon, Grid, Typography, Tag } from "@ctra/components";

import { useFarm } from "@farms";
import { LayoutProvider, SectionVariant } from "@analytics";
import { CompiledRoutes, Routes } from "@routes";
import { GAActions } from "../../../../analytics";

import styles from "./TabNavigation.module.less";

const { Text } = Typography;

/**
 * Tabular navigation between supported chart sections
 * @return {JSX.Element}
 * @constructor
 */
export const TabNavigation: FC = () => {
  const { t } = useTranslation();
  const { sectionSlug } = useParams<{ sectionSlug: SectionEntity["slug"] }>();
  const { farm } = useFarm();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();
  const { md } = Grid.useBreakpoint();

  const {
    ui: { beta },
    layouts: {
      section: { title: sectionTitle, favourite },
      dashboard: { compareCharts }
    }
  } = Content;

  const favouriteSection = {
    label: t(favourite),
    key: "favourites"
  };

  useEffect(() => {
    /**
     * Route to analytics/favourite as default if no other section is selected
     */
    if (!sectionSlug) {
      Enterprise.history.push(
        CompiledRoutes.app.analytics.section.index({
          sectionSlug: favouriteSection.key
        })
      );
    }
  }, [favouriteSection.key, sectionSlug]);

  return (
    <LayoutProvider farmID={farm?.id}>
      {({ supportedSections }, { isLoading: isLayoutLoading }) => {
        const items: ComponentProps<typeof Menu>["items"] = _.map(
          supportedSections,
          ({ slug, title, flags: { isNew } }) => {
            const text = t<string>(sectionTitle(title), {
              variant: farm ? SectionVariant.farm : SectionVariant.overview,
              makeDefaultValue: true
            });

            return {
              label: isNew ? (
                <>
                  {text} <Tag className={styles.BetaTag}>{t<string>(beta)}</Tag>
                </>
              ) : (
                text
              ),
              key: slug
            };
          }
        );

        const menu = (
          <Menu
            className={styles.TabNavigation}
            onClick={({ key }) =>
              Enterprise.history.push(
                CompiledRoutes.app.analytics.section.index({
                  sectionSlug: key
                })
              )
            }
            selectedKeys={[sectionSlug]}
            mode="horizontal"
            items={[favouriteSection, ...items]}
          />
        );

        return isLayoutLoading ? (
          <Spin />
        ) : md ? (
          <Row justify="space-between" align="middle" gutter={[16, 0]} wrap={false}>
            <Col flex={1}>{menu}</Col>
            <Col>
              <Button
                type="secondary"
                icon={<Icon component={Analysis} />}
                onClick={() => {
                  /* istanbul ignore next */
                  trackEvent(GAActions.goToCompareCharts, { label });

                  Enterprise.history.push(Routes.app.analytics.compareCharts.index);
                }}
              >
                {t(compareCharts.title)}
              </Button>
            </Col>
          </Row>
        ) : (
          menu
        );
      }}
    </LayoutProvider>
  );
};
